import type { CoralogixDomain } from '@coralogix/browser';

type CoralogixRumSdk = (typeof import('@coralogix/browser'))['CoralogixRum'];

/**
 * Frontend observability — Coralogix Browser RUM SDK.
 *
 * The SDK auto-captures uncaught errors, unhandled promise rejections, console
 * output and network activity once initialised. We add a manual `reportError`
 * for cases React swallows (render errors caught by the ErrorBoundary) and for
 * routing our existing `trackError` through to Coralogix.
 *
 * Init is gated: browser-only (no SSR/build), requires `GATSBY_CORALOGIX_PUBLIC_KEY`,
 * and only runs for environments we intend to monitor. Unset key → no-op.
 * The SDK is imported lazily inside `initObservability` so this module stays
 * side-effect free during SSR/build (it is reachable from gatsby-ssr via ErrorBoundary).
 */

const CORALOGIX_KEY = process.env.GATSBY_CORALOGIX_PUBLIC_KEY;
const APP_NAME = process.env.GATSBY_CORALOGIX_APP_NAME || 'mdcl-frontend';
const ENV = process.env.GATSBY_ENV || 'development';

// Allowed Coralogix regions; a mis-typed env value falls back to US1 instead of
// failing SDK init at runtime.
const CORALOGIX_DOMAINS: readonly CoralogixDomain[] = ['US1', 'US2', 'EU1', 'EU2', 'AP1', 'AP2', 'AP3'];
const resolveDomain = (value?: string): CoralogixDomain =>
    CORALOGIX_DOMAINS.find((domain) => domain === value) ?? 'US1';
const DOMAIN = resolveDomain(process.env.GATSBY_CORALOGIX_DOMAIN);

// Environments where we ship telemetry. Local dev stays quiet unless a key is
// explicitly provided AND the env is one of these.
const MONITORED_ENVS = new Set(['prod', 'qa']);

// Keys whose presence means the value is sensitive and must never leave the browser.
const SENSITIVE_KEY = /apikey|api_key|token|password|secret|authorization|auth|cookie|session|email/i;

const REDACTED = '[redacted]';

// Token check instead of a global email regex: an unanchored `[^\s@]+@…` rescans
// from every start position (super-linear on long input).
// Leading `@`s (mentions like `@john.doe@example.com`) are stripped first so the
// address behind them is still detected.
const looksLikeEmail = (token: string): boolean => {
    const candidate = token.replace(/^@+/, '');
    const at = candidate.indexOf('@');
    if (at < 1) return false;
    const dot = candidate.indexOf('.', at + 2);
    return dot !== -1 && dot < candidate.length - 1;
};

const redactEmails = (value: string): string =>
    value
        .split(/(\s+)/)
        .map((token) => (looksLikeEmail(token) ? REDACTED : token))
        .join('');

export const isObservabilityEnabled = (): boolean =>
    typeof window !== 'undefined' && !!CORALOGIX_KEY && MONITORED_ENVS.has(ENV);

/**
 * Drop sensitive keys and redact anything that looks like an email from a
 * (shallow) context object before it is sent to Coralogix. Nested objects are
 * dropped wholesale rather than deep-walked — cheaper and safer than trying to
 * recurse arbitrary payloads.
 */
export const sanitize = (context?: Record<string, unknown>): Record<string, unknown> => {
    if (!context) return {};
    const clean: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(context)) {
        if (SENSITIVE_KEY.test(key)) continue;
        if (typeof value === 'string') {
            clean[key] = redactEmails(value);
        } else if (value === null || typeof value !== 'object') {
            clean[key] = value;
        }
        // Nested objects are dropped, not deep-sanitised. Flatten the context
        // at the call site if a nested field must be reported.
    }
    return clean;
};

// Populated once the lazily-imported SDK initialises; stays null during SSR/build.
let rum: CoralogixRumSdk | null = null;

export const initObservability = (version: string): void => {
    if (!isObservabilityEnabled() || rum) return;

    import('@coralogix/browser')
        .then(({ CoralogixRum }) => {
            if (!CoralogixRum.isInited) {
                CoralogixRum.init({
                    public_key: CORALOGIX_KEY,
                    application: APP_NAME,
                    version,
                    coralogixDomain: DOMAIN,
                    environment: ENV,
                    sessionConfig: {
                        sessionSampleRate: 10,
                        alwaysTrackSessionsWithErrors: true,
                        excludeFromSampling: ['errors', 'custom'],
                    },
                    // Quota control: web vitals / resource timings / long tasks are pure
                    // volume and we never query them. Unlisted instrumentations stay on
                    // (SDK defaults everything to true), so errors + network still ship.
                    instrumentations: {
                        web_vitals: false,
                        resources: false,
                        long_tasks: false,
                    },
                });
            }
            rum = CoralogixRum;
        })
        .catch(() => {
            // Observability must never break the app; a failed chunk load is a silent no-op.
        });
};

/** Emit a structured error log to Coralogix. No-op when the SDK is not initialised. */
export const reportError = (message: string, context?: Record<string, unknown>): void => {
    if (!rum?.isInited) return;
    rum.error(message, sanitize(context));
};
